import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Globe, Menu, X, Network as NetworkIcon, Facebook } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSocial } from '../contexts/SocialContext';

export const Header = () => {
  const { language, setLanguage, t, languages } = useLanguage();
  const { social } = useSocial();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const currentLang = languages.find((l) => l.code === language) || languages[0];
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Navigate to landing page section. Works from any sub-page (e.g. /impressum,
  // /datenschutz) by going to "/" with a hash, then the LandingPage scrolls to it.
  const scrollToSection = (id) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/#' + id);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-black/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('home')}>
            <img
              src="https://customer-assets.emergentagent.com/job_4200a022-95b9-45c6-891b-2374fa355080/artifacts/ar8zvicq_color1_logo_transparent_background_ohne%20Rand.png"
              alt="Löwenherz Logo"
              className="h-12 md:h-16 w-auto transition-transform duration-300 hover:scale-105"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('home')}
              className="text-gray-200 hover:text-[#C4A55A] transition-colors duration-300 font-medium"
            >
              {t.nav.home}
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-gray-200 hover:text-[#C4A55A] transition-colors duration-300 font-medium"
            >
              {t.nav.about}
            </button>
            <button
              onClick={() => scrollToSection('aktienregister')}
              className="text-gray-200 hover:text-[#C4A55A] transition-colors duration-300 font-medium relative group"
              data-testid="nav-shares-desktop"
            >
              {t.nav.shares}
              <span className="absolute -top-1 -right-3 h-2 w-2 rounded-full bg-[#C4A55A] animate-pulse" />
            </button>
            <button
              onClick={() => scrollToSection('projects')}
              className="text-gray-200 hover:text-[#C4A55A] transition-colors duration-300 font-medium"
            >
              {t.nav.projects}
            </button>
            <button
              onClick={() => scrollToSection('investors')}
              className="text-gray-200 hover:text-[#C4A55A] transition-colors duration-300 font-medium"
            >
              {t.nav.investors}
            </button>
            <button
              onClick={() => scrollToSection('downloads')}
              className="text-gray-200 hover:text-[#C4A55A] transition-colors duration-300 font-medium"
              data-testid="nav-downloads-desktop"
            >
              {t.nav.downloads}
            </button>
            <Link
              to="/blog"
              className="text-gray-200 hover:text-[#C4A55A] transition-colors duration-300 font-medium"
              data-testid="nav-blog-desktop"
            >
              Blog
            </Link>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-200 hover:text-[#C4A55A] transition-colors duration-300 font-medium"
            >
              {t.nav.contact}
            </button>
          </nav>

          {/* Language Toggle & Mobile Menu */}
          <div className="flex items-center gap-2">
            {social.facebookUrl && (
              <a
                href={social.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Facebook"
                data-testid="header-fb-link"
                className="hidden md:inline-flex items-center justify-center h-8 w-8 rounded-md border border-white/10 hover:border-[#1877F2]/60 text-gray-400 hover:text-[#4f97ff] transition-colors"
              >
                <Facebook className="h-4 w-4" />
              </a>
            )}
            <Link
              to="/vermittler/login"
              data-testid="header-mediator-link"
              className="hidden md:inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-[#C4A55A] transition-colors px-3 py-1.5 rounded-md border border-white/10 hover:border-[#C4A55A]/40"
              title="Vermittler-Login & -Registrierung"
            >
              <NetworkIcon className="h-3.5 w-3.5" />
              Vermittler
            </Link>
            <div className="relative">
              <Button
                onClick={() => setShowLangMenu((v) => !v)}
                variant="ghost"
                size="sm"
                data-testid="header-lang-toggle"
                className="text-gray-200 hover:text-[#C4A55A] hover:bg-[#C4A55A]/10 transition-colors duration-300"
              >
                <Globe className="h-4 w-4 mr-2" />
                {currentLang.short}
              </Button>
              {showLangMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowLangMenu(false)}
                  />
                  <div
                    className="absolute right-0 mt-2 w-44 bg-[#1a1a1a] border border-[#C4A55A]/30 rounded-lg shadow-xl shadow-black/40 overflow-hidden z-50"
                    data-testid="header-lang-menu"
                  >
                    {languages.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => {
                          setLanguage(l.code);
                          setShowLangMenu(false);
                        }}
                        data-testid={`header-lang-${l.code}`}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                          language === l.code
                            ? 'bg-[#C4A55A]/15 text-[#C4A55A]'
                            : 'text-gray-200 hover:bg-white/5'
                        }`}
                      >
                        <span className="text-base leading-none">{l.flag}</span>
                        <span className="font-medium">{l.label}</span>
                        <span className="ml-auto text-xs text-gray-500">{l.short}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-gray-200 hover:text-[#C4A55A] transition-colors duration-300"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 space-y-4 border-t border-[#C4A55A]/20 pt-4">
            <button
              onClick={() => scrollToSection('home')}
              className="block text-gray-200 hover:text-[#C4A55A] transition-colors duration-300 font-medium"
            >
              {t.nav.home}
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="block text-gray-200 hover:text-[#C4A55A] transition-colors duration-300 font-medium"
            >
              {t.nav.about}
            </button>
            <button
              onClick={() => scrollToSection('aktienregister')}
              className="block text-gray-200 hover:text-[#C4A55A] transition-colors duration-300 font-medium"
              data-testid="nav-shares-mobile"
            >
              {t.nav.shares}
            </button>
            <button
              onClick={() => scrollToSection('projects')}
              className="block text-gray-200 hover:text-[#C4A55A] transition-colors duration-300 font-medium"
            >
              {t.nav.projects}
            </button>
            <button
              onClick={() => scrollToSection('investors')}
              className="block text-gray-200 hover:text-[#C4A55A] transition-colors duration-300 font-medium"
            >
              {t.nav.investors}
            </button>
            <button
              onClick={() => scrollToSection('downloads')}
              className="block text-gray-200 hover:text-[#C4A55A] transition-colors duration-300 font-medium"
              data-testid="nav-downloads-mobile"
            >
              {t.nav.downloads}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="block text-gray-200 hover:text-[#C4A55A] transition-colors duration-300 font-medium"
            >
              {t.nav.contact}
            </button>
            <Link
              to="/vermittler/login"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-2 text-gray-400 hover:text-[#C4A55A] transition-colors duration-300 font-medium text-sm pt-2 border-t border-white/10"
              data-testid="header-mediator-link-mobile"
            >
              <NetworkIcon className="h-4 w-4" />
              Vermittler-Bereich
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
};